<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { useI18n } from 'vue-i18n'
import clone from 'lodash/clone'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import { ShoppingCartIcon } from '@heroicons/vue/24/outline'
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import { storeToRefs } from 'pinia'
import { BigNumber } from 'bignumber.js'
import type {
  BpProduct,
  BpProductModifier,
  BpProductModifierGroup,
  OrderItem,
  TwAlertOptions,
} from '~/definitions/AllDefinitions'
import { useCartStore } from '~/store/CartStore'
import { useMainStore } from '~/store/MainStore'
import { useProductStore } from '~/store/ProductStore'
import TwAlert from '~/components/TwAlert.vue'
import { canOrder } from '~/classes/HourUtils'
import { isLast } from '~/classes/Utils'

const emit = defineEmits(['toggle', 'location-required'])
const { t } = useI18n()
interface ProductInfoInterface {
  open: boolean
  initialProduct: BpProduct | null
  initialOrderItem?: OrderItem | null
}
const props = withDefaults(defineProps<ProductInfoInterface>(), {
  open: false,
  initialProduct: null,
  initialOrderItem: null,
})
const currentAlert = ref({
  display: false,
  type: 'error',
  headline: t('PRODUCT_SOLD_OUT_ERROR'),
} as TwAlertOptions)
const loading = ref(true)
const options = ref([] as BpProductModifierGroup[])
const extras = ref([] as BpProductModifierGroup[])
const cartStore = useCartStore()
const mainStore = useMainStore()
const productStore = useProductStore()
const { currencyCode, isOrderingEnabled, timeZone, currentLocations, locationId } = storeToRefs(mainStore)
const hasBilling = computed(() => mainStore.rootData.hasBilling)
const product = ref(null as BpProduct | null)
const defaultOrderItem = ref({
  quantity: 1,
  specialInstructions: '',
  options: {},
  extras: {},
} as OrderItem)
const orderItem = ref({
  quantity: 1,
  specialInstructions: '',
  options: {},
  extras: {},
} as OrderItem)
const locationOrderingEnabled = ref(false)
const enableOrdering = computed(() => {
  return locationOrderingEnabled.value &&
    !loading.value &&
    !currentAlert.value.display &&
    product.value &&
    canOrder(product.value, Boolean(isOrderingEnabled.value), timeZone.value)
})
const showNotAcceptingOrdersButton = computed(() => {
  return hasBilling.value && !enableOrdering.value && !currentAlert.value.display
})

// Helper to get days when a product is available (for day-restricted items)
function getProductAvailableDays(prod: BpProduct | null): string[] {
  if (!prod?.availability?.hours || prod.availability.default !== false) {
    return []
  }
  const availableDays: string[] = []
  const dayOrder = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']
  for (const day of dayOrder) {
    const dayHours = prod.availability.hours[day]
    if (dayHours && dayHours.type !== 'closed') {
      availableDays.push(day)
    }
  }
  return availableDays
}

// Computed to get the message for the unavailable button (priority: out of stock > day restrictions > generic)
const unavailableButtonMessage = computed(() => {
  if (!product.value) {
    return null
  }

  // Priority 1: Out of stock
  if (product.value.outOfStock) {
    return t('ITEM_CURRENTLY_OUT_OF_STOCK')
  }

  // Priority 2: Day restrictions (product has limited availability and is not available today)
  const availableDays = getProductAvailableDays(product.value)
  if (availableDays.length > 0 && availableDays.length < 7) {
    // Translate day codes to full names
    const dayNames = availableDays.map(day => t(day))
    return t('ITEM_ONLY_AVAILABLE_ON', { days: dayNames.join(', ') })
  }

  // Priority 3: Generic message
  return t('CURRENTLY_NOT_ACCEPTING_ONLINE_ORDERS')
})
const disableAddToBag = computed(() => {
  if (!enableOrdering.value) {
    return true
  }

  // Validate required options groups (existing logic)
  if (options.value.length) {
    const selectedOptionsCount = Object.values(orderItem.value.options).length
    if (selectedOptionsCount < options.value.length) {
      return true
    }
  }

  // Validate min_selections for all modifier groups
  const allGroups = [...options.value, ...extras.value]
  for (const group of allGroups) {
    if (group.minSelections !== null && group.minSelections > 0) {
      const selectedCount = getSelectedCountForGroup(group)
      if (selectedCount < group.minSelections) {
        return true // Not enough selections
      }
    }
  }

  return false
})

// Helper function to count selections for a group
function getSelectedCountForGroup(group: BpProductModifierGroup): number {
  if (group.type === 'options') {
    if (group.maxSelections === 1) {
      // Single selection (radio): stored as object
      return orderItem.value.options[group.uuid] ? 1 : 0
    } else {
      // Multiple selection (checkbox): stored as array
      const selections = orderItem.value.options[group.uuid] || []
      return Array.isArray(selections) ? selections.length : 0
    }
  } else {
    // Extras: always stored as array
    const selections = orderItem.value.extras[group.uuid] || []
    return Array.isArray(selections) ? selections.length : 0
  }
}

// Helper function to display selection requirement text
function getSelectionRequirementText(group: BpProductModifierGroup): string {
  const min = group.minSelections ?? 0
  const max = group.maxSelections ?? null

  // If min and max both equal 1, don't show (obvious from radio buttons)
  if (min === 1 && max === 1) {
    return ''
  }

  // If min === max, show "Select X"
  if (min === max && min > 0) {
    return t('SELECT_N', { count: min })
  }

  // If only min (no max), show "At least X"
  if (min > 0 && max === null) {
    return t('SELECT_AT_LEAST', { count: min })
  }

  // If only max, show "Select up to X"
  if (min === 0 && max !== null && max > 1) {
    return t('SELECT_UP_TO', { count: max })
  }

  // If both min and max are different, show "Select X to Y"
  if (min > 0 && max !== null && min !== max) {
    return t('SELECT_BETWEEN', { min, max })
  }

  // Optional
  if (min === 0 && max === null) {
    return t('OPTIONAL')
  }

  return group.required ? t('REQUIRED') : t('OPTIONAL')
}

// Helper function to determine if a checkbox should be disabled
function isCheckboxDisabled(
  modifierUuid: string,
  groupUuid: string,
  maxSelections: number | null,
  isOptionsGroup: boolean,
): boolean {
  if (maxSelections === null || maxSelections === 0) {
    return false // Unlimited selections
  }

  const selections = isOptionsGroup
    ? (orderItem.value.options[groupUuid] || [])
    : (orderItem.value.extras[groupUuid] || [])

  if (!Array.isArray(selections)) {
    return false // Not a checkbox group
  }

  const isSelected = selections.some(mod => mod.uuid === modifierUuid)
  if (isSelected) {
    return false // Already selected, allow unselecting
  }

  // Not selected and max reached
  return selections.length >= maxSelections
}

const price = computed(() => {
  let total = new BigNumber(product.value?.price ?? 0)

  // Options: can be single object (radio) or array (checkbox) based on maxSelections
  for (const [_, selected] of Object.entries(orderItem.value.options ?? {})) {
    if (Array.isArray(selected)) {
      // Multi-select checkbox: array of modifiers
      selected.forEach((mod) => {
        if (mod) {
          total = total.plus(new BigNumber(mod.price || 0))
        }
      })
    } else if (selected) {
      // Single-select radio: single object
      total = total.plus(new BigNumber(selected.price || 0))
    }
  }

  // Extras: multiple modifiers per group
  for (const [groupUuid, modifiers] of Object.entries(orderItem.value.extras ?? {})) {
    if (!Array.isArray(modifiers)) {
      continue
    }

    const modifierGroup = product.value?.productModifierGroups?.find(group => group.uuid === groupUuid)
    const maxSelections = modifierGroup?.maxSelections ?? null
    const pricePerExtra = modifierGroup?.pricePerExtra ?? null

    modifiers.forEach((mod, index) => {
      if (!mod) {
        return
      }

      if (maxSelections == null) {
        // Unlimited selections → always use modifier's own price
        total = total.plus(new BigNumber(mod.price || 0))
      } else if (index < maxSelections) {
        // Within free limit → use modifier's own price
        total = total.plus(new BigNumber(mod.price || 0))
      } else if (pricePerExtra != null) {
        // Exceeds limit → use pricePerExtra
        total = total.plus(new BigNumber(pricePerExtra))
      } else {
        // Fallback to own price if no pricePerExtra is set
        total = total.plus(new BigNumber(mod.price || 0))
      }
    })
  }
  return total.multipliedBy(orderItem.value.quantity ?? 1).toNumber()
})
const thumbnail = computed(() => {
  const defImg =
    product.value && product.value.image && product.value.image.original
      ? product.value.image.original
      : 'https://twyzle-s3-1.s3.amazonaws.com/libraries/assets/icons/menu-item-default_200x200.jpg'
  if (!product.value) {
    return defImg
  }
  return product.value.image && product.value.image.sizes
    ? product.value.image.sizes.s200
      ? product.value.image.sizes.s200
      : product.value.image.sizes.s120
        ? product.value.image.sizes.s120
        : defImg
    : defImg
})
async function addToCart() {
  // Check if location is selected for multi-location accounts
  if (currentLocations.value && currentLocations.value.length > 1 && !locationId.value) {
    emit('location-required')
    return
  }

  loading.value = true
  currentAlert.value.display = false
  const item = await getItem()
  if (!item) {
    locationOrderingEnabled.value = false
    if (product.value) {
      product.value.outOfStock = true
    }
    currentAlert.value.headline = t('PRODUCT_OUT_OF_STOCK')
    currentAlert.value.display = true
    loading.value = false
  } else {
    locationOrderingEnabled.value = item.isOrderingEnabled
    const canAddToCart = canOrder(item.item, Boolean(isOrderingEnabled.value), timeZone.value)
    product.value = item.item as BpProduct
    if (!canAddToCart) {
      currentAlert.value.headline = t('PRODUCT_OUT_OF_STOCK')
      currentAlert.value.display = true
      loading.value = false
    } else {
      const body = {
        id: clone(product.value.id),
        quantity: clone(orderItem.value.quantity),
        specialInstructions: clone(orderItem.value.specialInstructions),
        shopCategoryId: clone(product.value.shopCategoryId),
      } as any
      if (orderItem.value.options || orderItem.value.extras.length) {
        body.modifiers = []
        if (Object.keys(orderItem.value.extras).length) {
          for (const extraKey in orderItem.value.extras) {
            body.modifiers = body.modifiers.concat(orderItem.value.extras[extraKey])
          }
        }
        if (Object.keys(orderItem.value.options).length) {
          for (const optionKey in orderItem.value.options) {
            body.modifiers = body.modifiers.concat(orderItem.value.options[optionKey])
          }
        }
      }
      const res = await cartStore.createCartItem(body)
      if (res.success) {
        loading.value = false
        toggleOpen()
      } else {
        currentAlert.value.headline = t('ADD_TO_CART_ERROR')
        currentAlert.value.display = true
        loading.value = false
      }
    }
  }
}
async function getItem() {
  if (props.initialProduct) {
    const { shopCategoryId, id } = props.initialProduct
    const res = await productStore.getItem(Number(shopCategoryId), Number(id))
    return res.data
  } else {
    return null
  }
}
function getModifierPrice(extra: BpProductModifier, modifierGroup: BpProductModifierGroup) {
  const selected = orderItem.value.extras[modifierGroup.uuid] || []
  const selectedUuids = selected.map(mod => mod.uuid)
  const selectedIndex = selectedUuids.indexOf(extra.uuid)
  const isSelected = selectedIndex !== -1
  const selectedCount = selected.length

  // If unlimited selections, always show modifier price
  if (!modifierGroup.maxSelections && modifierGroup.maxSelections !== 0) {
    return typeof extra.price !== 'undefined' ? extra.price : 0
  }
  // Determine if this extra is "within free limit"
  const withinFreeLimit = isSelected && selectedIndex < (modifierGroup.maxSelections || Infinity)

  if (withinFreeLimit) {
    return typeof extra.price !== 'undefined' ? extra.price : 0
  }

  if (
    modifierGroup.pricePerExtra &&
    modifierGroup.maxSelections &&
    (
      (isSelected && selectedIndex >= modifierGroup.maxSelections) ||
      (!isSelected && selectedCount >= modifierGroup.maxSelections)
    )
  ) {
    return modifierGroup.pricePerExtra
  }

  return typeof extra.price !== 'undefined' ? extra.price : 0
}
async function getProduct() {
  options.value.length = 0
  extras.value.length = 0
  resetOrderItem()
  loading.value = true
  const item = await getItem()
  if (item && item.item) {
    locationOrderingEnabled.value = item.isOrderingEnabled
    product.value = item.item as BpProduct
    if (product.value.productModifierGroups?.length) {
      for (const mod of product.value.productModifierGroups) {
        const copy = cloneDeep(mod) as any
        if (mod.type === 'options') {
          options.value.push(copy)
        } else {
          extras.value.push(copy)
        }
      }
      // Initialize arrays for multi-select options groups
      for (const group of options.value) {
        if (group.maxSelections !== 1) {
          if (!Array.isArray(orderItem.value.options[group.uuid])) {
            orderItem.value.options[group.uuid] = []
          }
        }
      }
      // Initialize arrays for extras groups (always multi-select)
      for (const group of extras.value) {
        if (!Array.isArray(orderItem.value.extras[group.uuid])) {
          orderItem.value.extras[group.uuid] = []
        }
      }
    }
  } else {
    locationOrderingEnabled.value = false
  }
  loading.value = false
}
function resetOrderItem() {
  if (props.initialOrderItem) {
    orderItem.value = cloneDeep(props.initialOrderItem)
  } else {
    orderItem.value.quantity = clone(defaultOrderItem.value.quantity)
    orderItem.value.specialInstructions = clone(defaultOrderItem.value.specialInstructions)
    orderItem.value.extras = {}
    orderItem.value.options = {}
  }
}
function toggleOpen() {
  if (currentLocations.value && currentLocations.value.length > 1 && !locationId.value) {
    emit('location-required')
    return
  }
  emit('toggle')
}
function updateQuantity(num: number) {
  orderItem.value.quantity = orderItem.value.quantity + num
}
onMounted(() => {
  currentAlert.value.display = false
  getProduct()
})
watch(() => [props.open, props.initialProduct, props.initialOrderItem], (newValue, oldValue) => {
  if (!isEqual(newValue, oldValue)) {
    currentAlert.value.display = false
    getProduct()
  }
})
</script>

<template>
  <TransitionRoot
    as="template"
    :show="open"
  >
    <Dialog
      as="div"
      class="relative z-40"
      @close="toggleOpen()"
    >
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="tw-dialog-transition-child" />
      </TransitionChild>

      <div class="fixed inset-0 z-30 w-screen overflow-y-auto">
        <div class="flex min-h-full items-stretch justify-center text-center md:items-center md:px-2 lg:px-4">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
            enter-to="opacity-100 translate-y-0 md:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 md:scale-100"
            leave-to="opacity-0 translate-y-4 md:translate-y-0 md:scale-95"
          >
            <DialogPanel class="tw-dialog-panel">
              <div>
                <button
                  :disabled="loading"
                  type="button"
                  class="tw-dialog-close-button"
                  @click="toggleOpen"
                >
                  <span class="absolute -inset-0.5" />
                  <span class="sr-only">{{ t('CLOSE') }}</span>
                  <XMarkIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                </button>

                <div class="grid grid-rows-[max-content_1fr] sm:grid-rows-1 sm:grid-cols-[auto_1fr] w-full gap-x-6 gap-y-8 lg:gap-x-8 h-full">
                  <div class="sm:col-span-1">
                    <div
                      v-if="loading"
                      class="h-[200px] w-[200px] rounded-lg bg-slate-200 dark:bg-slate-600 animate-pulse"
                    />
                    <div
                      v-else-if="product"
                      class="h-[200px] w-[200px] rounded-lg border border-tw-1 bg-cover bg-center bg-no-repeat"
                      :style="{ 'background-image': 'url(' + thumbnail + ')' }"
                    />
                  </div>
                  <div
                    v-if="!loading && product"
                    class="sm:col-span-1 h-full flex flex-col justify-between"
                  >
                    <div>
                      <h2 class="text-2xl font-semibold text-slate-700 dark:text-slate-50 sm:pr-12">
                        {{ product.name }}
                      </h2>
                      <section
                        aria-labelledby="information-heading"
                        class="mt-2"
                      >
                        <h3
                          id="information-heading"
                          class="sr-only"
                        >
                          {{ t('PRODUCT_INFORMATION') }}
                        </h3>
                        <p
                          v-if="!options.length || product.outOfStock"
                          class="text-2xl text-slate-700 dark:text-slate-50"
                        >
                          <span v-if="!options.length">
                            {{ $filters.formatCurrency(product.price, currencyCode) }}
                          </span>
                          <span
                            v-if="product.outOfStock"
                            class="text-rose-400"
                          >
                            - {{ t('OUT_OF_STOCK') }}
                          </span>
                        </p>
                        <div
                          v-if="product.description"
                          class="mt-2"
                        >
                          <h4 class="sr-only">
                            {{ t('DESCRIPTION') }}
                          </h4>
                          <p class="text-sm text-slate-600 dark:text-slate-400 whitespace-pre-line">
                            {{ product.description }}
                          </p>
                        </div>
                        <div
                          v-if="enableOrdering"
                          class="mt-5"
                        >
                          <div class="mb-2 label-primary">
                            {{ t('QUANTITY') }}
                          </div>
                          <span class="quantity-container">
                            <button
                              type="button"
                              class="rounded-l-md quantity-button"
                              :disabled="orderItem.quantity <= 1"
                              @click="updateQuantity(-1)"
                            >
                              <span class="sr-only">{{ t('MINUS_ONE') }}</span>
                              <ChevronLeftIcon
                                class="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                            <button
                              type="button"
                              disabled
                              class="-ml-px px-5"
                            >
                              {{ orderItem.quantity }}
                            </button>
                            <button
                              :disabled="orderItem.quantity >= 100"
                              type="button"
                              class="-ml-px rounded-r-md quantity-button"
                              @click="updateQuantity(1)"
                            >
                              <span class="sr-only">{{ t('PLUS_ONE') }}</span>
                              <ChevronRightIcon
                                class="h-5 w-5"
                                aria-hidden="true"
                              />
                            </button>
                          </span>
                        </div>
                        <div
                          v-else-if="options.length || extras.length"
                          class="w-full mt-5 text-tw-1"
                        >
                          <div
                            v-if="options.length"
                            class="w-full"
                          >
                            <div
                              v-for="(optionModGroup, optionModGroupIdx) in options"
                              :key="optionModGroup.uuid"
                              :class="{
                                'mb-4': !isLast(options, optionModGroupIdx),
                              }"
                            >
                              <label class="label-primary mb-2 text-base">
                                {{ optionModGroup.name }}
                              </label>
                              <div
                                v-for="(option, optionIdx) in optionModGroup.modifiers"
                                :key="option.uuid"
                                :class="{ 'mb-2 pb-2 border-b border-tw-1': !isLast(optionModGroup.modifiers, optionIdx) }"
                                class="w-full flex flex-row justify-between text-sm"
                              >
                                <div>{{ option.name }}</div>
                                <div v-if="option.price > 0">
                                  {{ $filters.formatCurrency(option.price, currencyCode) }}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            v-if="extras.length"
                            :class="{ 'mt-10': options.length }"
                            class="w-full"
                          >
                            <div
                              v-for="(extrasModGroup, extrasModGroupIdx) in extras"
                              :key="extrasModGroup.uuid"
                              :class="{
                                'mb-5': !isLast(options, extrasModGroupIdx),
                              }"
                            >
                              <label class="label-primary mb-2 text-base">
                                {{ extrasModGroup.name }}
                              </label>
                              <div
                                v-for="(extra, optionIdx) in extrasModGroup.modifiers"
                                :key="extra.uuid"
                                :class="{ 'mb-2 pb-2 border-b border-tw-1': !isLast(extrasModGroup.modifiers, optionIdx) }"
                                class="w-full flex flex-row justify-between text-sm"
                              >
                                <div>{{ extra.name }}</div>
                                <div>
                                  + {{ $filters.formatCurrency(extra.price, currencyCode) }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </section>
                    </div>
                    <section
                      v-if="enableOrdering"
                      aria-labelledby="options-heading"
                      class="grow"
                    >
                      <h3
                        id="options-heading"
                        class="sr-only"
                      >
                        Product options
                      </h3>
                      <VeeForm class="flex flex-col h-full">
                        <div
                          v-if="options.length"
                          class="w-full mt-6"
                        >
                          <div
                            v-for="(modifierGroup, modifierGroupIdx) in options"
                            :key="modifierGroup.uuid"
                            :class="{ 'mb-4': modifierGroupIdx !== options.length - 1 }"
                          >
                            <div class="label-primary mb-2 flex flex-col">
                              {{ modifierGroup.name }}
                              <span class="text-xs text-slate-600">
                                {{ getSelectionRequirementText(modifierGroup) }}
                              </span>
                            </div>
                            <fieldset>
                              <div class="space-y-2">
                                <div
                                  v-for="option in modifierGroup.modifiers"
                                  :key="option.uuid"
                                  class="flex items-center"
                                >
                                  <!-- Radio button for single selection (maxSelections === 1) -->
                                  <input
                                    v-if="modifierGroup.maxSelections === 1"
                                    :id="`modifier-${option.uuid}`"
                                    v-model="orderItem.options![modifierGroup.uuid]"
                                    :name="`modifier-${modifierGroup.uuid}`"
                                    type="radio"
                                    :value="option"
                                    class="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                  >
                                  <!-- Checkbox for multiple selections (maxSelections !== 1) -->
                                  <input
                                    v-else
                                    :id="`modifier-${option.uuid}`"
                                    v-model="orderItem.options![modifierGroup.uuid]"
                                    type="checkbox"
                                    :value="option"
                                    :disabled="isCheckboxDisabled(
                                      option.uuid,
                                      modifierGroup.uuid,
                                      modifierGroup.maxSelections,
                                      true,
                                    )"
                                    class="tw-checkbox"
                                  >
                                  <label
                                    :for="`modifier-${option.uuid}`"
                                    class="ml-3 block text-sm font-medium leading-6 text-tw-2"
                                  >
                                    {{ option.name }}
                                    <span v-if="option.price > 0">
                                      + {{ $filters.formatCurrency(option.price, currencyCode) }}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        </div>
                        <div
                          v-if="extras.length"
                          :class="{ 'mt-5': options.length }"
                          class="w-full"
                        >
                          <div
                            v-for="(modifierGroup) in extras"
                            :key="'extras-' + modifierGroup.uuid"
                          >
                            <div class="label-primary mb-2">
                              {{ modifierGroup.name }}
                              <span class="text-xs text-slate-600">
                                ({{ getSelectionRequirementText(modifierGroup) }})
                              </span>
                              <p
                                v-if="modifierGroup.maxSelections && modifierGroup.pricePerExtra"
                                class="text-xs"
                              >
                                <span v-html="t('SELECT_X_FREE', { value: modifierGroup.maxSelections })" />.
                                <!-- eslint-disable-next-line -->
                                <span v-html="t('EXTRAS_ARE_X', { value: $filters.formatCurrency(modifierGroup.pricePerExtra, currencyCode) })"/>
                              </p>
                            </div>

                            <fieldset>
                              <div class="space-y-2">
                                <div
                                  v-for="extra in modifierGroup.modifiers"
                                  :key="extra.uuid"
                                  class="flex items-center"
                                >
                                  <input
                                    :id="`modifier-${extra.uuid}`"
                                    v-model="orderItem.extras![modifierGroup.uuid]"
                                    name="modifier"
                                    :value="extra"
                                    type="checkbox"
                                    class="tw-checkbox"
                                  >
                                  <label
                                    :for="`modifier-${extra.uuid}`"
                                    class="ml-3 block text-sm font-medium leading-6 text-tw-2"
                                  >
                                    {{ extra.name }}
                                    <span v-if="getModifierPrice(extra, modifierGroup) !== null">
                                      + {{
                                        $filters.formatCurrency(
                                          getModifierPrice(extra, modifierGroup) || 0,
                                          currencyCode,
                                        )
                                      }}
                                    </span>
                                  </label>
                                </div>
                              </div>
                            </fieldset>
                          </div>
                        </div>
                        <label
                          for="specialInstructions"
                          class="label-primary mt-5 mb-1"
                        >
                          {{ t('SPECIAL_INSTRUCTIONS') }}
                        </label>
                        <VeeField
                          id="specialInstructions"
                          v-model="orderItem.specialInstructions"
                          as="textarea"
                          name="specialInstructions"
                          maxlength="255"
                          :placeholder="t('WRITE_SPECIAL_INSTRUCTIONS')"
                          class="simple-input"
                        />
                        <div class="mt-1 flex justify-between label-primary mb-6 sm:mb-0">
                          <span>{{ t('SPECIAL_INSTRUCTIONS_DISCLAIMER') }}</span>
                          <span>{{ (orderItem.specialInstructions || '').length }}/255</span>
                        </div>
                        <div class="mt-auto sm:mt-6">
                          <button
                            :disabled="disableAddToBag"
                            type="submit"
                            class="tw-button primary"
                            @click="addToCart()"
                          >
                            <ShoppingCartIcon class="text-accent-contrast mr-2 w-5 h-5" />
                            {{ t('ADD_TO_CART') }} &mdash; {{ $filters.formatCurrency(price, currencyCode) }}
                          </button>
                        </div>
                      </VeeForm>
                    </section>
                    <div
                      v-if="showNotAcceptingOrdersButton"
                      class="mt-6"
                    >
                      <button
                        disabled
                        type="button"
                        class="tw-button primary"
                      >
                        {{ unavailableButtonMessage }}
                      </button>
                    </div>
                    <tw-alert
                      v-if="currentAlert.display"
                      :headline="currentAlert.headline"
                      :items="currentAlert.items"
                      :sticky="false"
                      :disable-dismiss="true"
                      class="mt-5"
                    />
                  </div>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
